// NATIVE
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// INSTALLED
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Box, Grid, Paper, Avatar, Button, Typography, CssBaseline, ThemeProvider, alpha, Stack } from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useDispatch } from 'react-redux';

// CUSTOM
import { theme } from '../commons/theme';
import { CopyRight } from '../components';
import { startResetPassword } from '../store/auth';
import { SnackAlert } from '../components/SnackAlert';
import QiPasswordField from '../components/formFields/QiPasswordField';

const PasswordRequirement = ({ label, met }: { label: string; met: boolean }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        {met ? (
            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
            <CancelIcon sx={{ fontSize: 16, color: 'error.main', opacity: 0.5 }} />
        )}
        <Typography variant="caption" sx={{ color: met ? 'success.main' : 'text.secondary', fontWeight: met ? 600 : 400 }}>
            {label}
        </Typography>
    </Stack>
);

const passwordRequirements = [
    { label: 'Mínimo 8 caracteres', regex: /.{8,}/ },
    { label: 'Al menos una mayúscula', regex: /[A-Z]/ },
    { label: 'Al menos una minúscula', regex: /[a-z]/ },
    { label: 'Al menos un número', regex: /[0-9]/ },
    { label: 'Al menos un carácter especial', regex: /[!@#$%^&*(),.?":{}|<>]/ },
];

export const ResetPasswordPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [alert, setAlert] = useState({ open: false, msg: '', status: 'success' });

    // Obtener token de la URL
    const query = new URLSearchParams(location.search);
    const token = query.get('token');

    const validationSchema = Yup.object().shape({
        password: Yup.string()
            .required('La contraseña es obligatoria')
            .min(8, 'Mínimo 8 caracteres')
            .matches(/[A-Z]/, 'Debe tener una mayúscula')
            .matches(/[a-z]/, 'Debe tener una minúscula')
            .matches(/[0-9]/, 'Debe tener un número')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Debe tener un carácter especial'),
        confirmPassword: Yup.string()
            .required('Confirma tu contraseña')
            .oneOf([Yup.ref('password')], 'Las contraseñas no coinciden')
    });

    const handleSubmit = async (values: any, { setSubmitting }: any) => {
        if (!token) {
            setAlert({ open: true, msg: 'Token de recuperación no encontrado', status: 'error' });
            setSubmitting(false);
            return;
        }

        const response = await dispatch(startResetPassword({
            token,
            password: values.password
        }));

        if (response && response.ok) {
            setAlert({ open: true, msg: 'Contraseña actualizada con éxito', status: 'success' });
            setTimeout(() => {
                navigate('/');
            }, 2000);
        } else {
            setAlert({ open: true, msg: response?.message || 'Error al restablecer contraseña', status: 'error' });
        }
        setSubmitting(false);
    };

    if (!token) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                    <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
                        <Typography variant="h6" color="error" gutterBottom>
                            Enlace inválido
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            El enlace de recuperación no es válido o ha expirado.
                        </Typography>
                        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
                            Volver al Inicio
                        </Button>
                    </Paper>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <Grid container component="main" sx={{ height: '100vh' }}>
                <CssBaseline />
                <Grid
                    size={{ sm:4, md:7  }}
                    sx={{
                        backgroundImage: 'url(/qiimagen.png)',
                        backgroundRepeat: 'no-repeat',
                        backgroundColor: (t) => t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
                        backgroundSize: '100% 100%',
                        backgroundPosition: 'center',
                        display: { xs: 'none', sm: 'block' }
                    }}
                />
                <Grid
                    size={{ xs: 12, sm: 8, md: 5 }}
                    component={Paper}
                    elevation={6}
                    square
                    sx={{
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        component="img"
                        src="/qiimagen-mobil.png"
                        sx={{
                          display: { xs: 'block', sm: 'none' },
                          width: '100%',
                          height: '35vh', 
                          objectFit: 'fill',
                        }}
                    />
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            px: 4,
                            py: { xs: 2, sm: 8 },
                            overflowY: 'auto'
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                            <Avatar sx={{ m: 0.5, bgcolor: 'primary.main', width: 40, height: 40 }}>
                                <LockResetIcon />
                            </Avatar>
                            <Typography component="h1" variant="h6" sx={{ fontWeight: 700 }}>
                                Nueva Contraseña
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', px: 2 }}>
                                Ingresa tu nueva contraseña para acceder a tu cuenta.
                            </Typography>
                        </Box>

                        <Formik
                            initialValues={{ password: '', confirmPassword: '' }}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting, values }) => (
                                <Form autoComplete="off">
                                    <Box sx={{ mt: 1, width: '100%' }}>
                                        <QiPasswordField
                                            margin="normal"
                                            required
                                            fullWidth
                                            name="password"
                                            label="Nueva Contraseña"
                                            id="password"
                                            color='primary'
                                        />

                                        <Box sx={{ mt: 1.5, px: 1, mb: 2 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'text.secondary' }}>
                                                Requisitos de seguridad:
                                            </Typography>
                                            {passwordRequirements.map((req, index) => (
                                                <PasswordRequirement 
                                                    key={index} 
                                                    label={req.label} 
                                                    met={req.regex.test(values.password)} 
                                                />
                                            ))}
                                        </Box>

                                        <QiPasswordField
                                            margin="normal"
                                            required
                                            fullWidth
                                            name="confirmPassword"
                                            label="Confirmar Contraseña"
                                            id="confirmPassword"
                                            color='primary'
                                        />

                                        <Button
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            disabled={isSubmitting}
                                            sx={{ 
                                                mt: 4, 
                                                mb: 2, 
                                                py: 1.5, 
                                                borderRadius: 2, 
                                                textTransform: 'uppercase',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {isSubmitting ? 'Guardando...' : 'Restablecer Contraseña'}
                                        </Button>

                                        <Button
                                            fullWidth
                                            variant="text"
                                            onClick={() => navigate('/')}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Volver al Login
                                        </Button>

                                        <CopyRight sx={{ mt: 5 }} />
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Grid>
            </Grid>
            <SnackAlert 
                open={alert.open} 
                onclick={(val: boolean) => setAlert({ ...alert, open: val })} 
                text={alert.msg} 
                severity={alert.status} 
            />
        </ThemeProvider>
    );
};
